import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle, faCheckCircle, faUsers, faUser, faXmark } from '@fortawesome/free-solid-svg-icons';

// Import Firebase services needed directly
import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";

// Define the callable function reference outside the component
const newUserCallable = httpsCallable(functions, 'newUser');

function CreateAccountPage() {
    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // State for form fields
    const [email, setEmail] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // State for operation status
    const [orgIdFromUrl, setOrgIdFromUrl] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // React Router hooks
    const { orgId } = useParams();
    const navigate = useNavigate();

    // Effect to handle orgId from URL
    useEffect(() => {
        if (orgId) {
            console.log("Organization ID detected from URL:", orgId);
            setOrgIdFromUrl(orgId);
        } else {
            console.log("No Organization ID provided in URL.");
            setOrgIdFromUrl(null);
        }
    }, [orgId]);

    // Form validation function
    const validateForm = () => {
        let isValid = true;
        let validationError = null;
        if (!email.trim() || !fullName.trim() || !password.trim() || !confirmPassword.trim()) {
            validationError = "All fields are required."; isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            validationError = "Please enter a valid email address."; isValid = false;
        } else if (password.length < 6) {
            validationError = "Password must be at least 6 characters long."; isValid = false;
        } else if (password !== confirmPassword) {
            validationError = "Passwords do not match."; isValid = false;
        }
        setError(validationError);
        return isValid;
    };

    // Form submission handler
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (isLoading || successMessage) {
            return;
        }
        if (!validateForm()) {
            return;
        }

        setError(null);
        setIsLoading(true);
        setSuccessMessage(null);

        const currentPassword = password;

        const payload = {
            email: email.trim(),
            fullName: fullName.trim(),
            password: currentPassword,
            orgId: orgIdFromUrl
        };

        console.log("Calling 'newUser' function with payload:", { ...payload, password: '***' });

        try {
            const result = await newUserCallable(payload);
            const resultData = result.data;
            console.log("'newUser' function result:", resultData);

            if (resultData?.success) {
                const createdEmail = email.trim();

                console.log(`Backend success. Attempting client-side sign in for ${createdEmail}...`);
                signInWithEmailAndPassword(auth, createdEmail, currentPassword)
                    .then(async (userCredential) => {
                        const user = userCredential.user;
                        console.log(`Client-side sign in successful for user ${user.uid}. Refreshing token to get claims...`);
                        await user.getIdToken(true);
                        console.log(`Token refreshed. Sending verification email...`);
                        sendEmailVerification(user)
                            .then(() => {
                                console.log("Verification email sent successfully.");
                                setSuccessMessage("Your account has been created! Please check your email to verify your address. Redirecting...");
                                setEmail(''); setFullName(''); setPassword(''); setConfirmPassword('');
                                setTimeout(() => { navigate('/dashboard'); }, 3500);
                            })
                            .catch((emailError) => {
                                console.error("Error sending verification email:", emailError);
                                setError(`Account created and signed in, but failed to send verification email: ${emailError.message}. Please try logging in and requesting verification again later.`);
                                setIsLoading(false);
                            });
                    })
                    .catch((signInError) => {
                        console.error("Error signing in new user after creation:", signInError);
                        setError(`Account created on backend, but failed to sign you in automatically: ${signInError.message}. Please try logging in manually.`);
                        setIsLoading(false);
                    });

            } else {
                const backendMessage = resultData?.message || "Failed to create account. Please try again.";
                const specificError = backendMessage.includes('already exists')
                    ? "An account with this email already exists. Try logging in instead."
                    : backendMessage;
                setError(specificError);
                setIsLoading(false);
            }
        } catch (err) {
            console.error("Error calling 'newUser' function:", err);
            let specificError = "An unexpected error occurred while creating the account.";
            if (err && err.code && err.message) {
                const code = err.code;
                const message = err.message;

                if (code === 'functions/already-exists' || message.includes('EMAIL_EXISTS')) {
                    specificError = "An account with this email address already exists. Try logging in.";
                } else if (code === 'functions/permission-denied') {
                    specificError = "Permission denied. You might not be allowed to join the linked organization, or the ID might be invalid.";
                } else if (code === 'functions/not-found') {
                    specificError = `Server error: Could not find the account creation service.`;
                } else if (code === 'functions/invalid-argument') {
                    specificError = "Invalid data provided to server. Please check your input.";
                } else {
                    specificError = `Server error: ${message} (code: ${code})`;
                }
            }
            setError(specificError);
            setIsLoading(false);
        }
    };

    // Determine if form inputs should be disabled
    const formInputsDisabled = isLoading || !!successMessage;

    return (
        <div className="create-account-page">
            {/* Hero Header */}
            <section className="create-account-hero">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 text-center">
                            <h1 className="create-account-title">
                                <FontAwesomeIcon icon={faUsers} className="me-3" />
                                Join Your Church Directory
                            </h1>
                            <p className="create-account-subtitle">
                                Create your personal account using the email address your church has on file.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="create-account-content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-6">
                            {/* Admin Redirect Notice */}
                            <p className="admin-redirect">
                                Setting up a new church directory? <Link to="/register-organization">Register as an administrator</Link>
                            </p>

                            {/* Registration Card */}
                            <div className="create-account-card">
                                {/* Error Display */}
                                {error && (
                                    <div className="alert alert-danger d-flex align-items-center" role="alert">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 flex-shrink-0" />
                                        <div>{error}</div>
                                    </div>
                                )}

                                {/* Success Display */}
                                {successMessage && (
                                    <div className="alert alert-success d-flex align-items-center" role="alert">
                                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 flex-shrink-0" />
                                        <div>{successMessage}</div>
                                    </div>
                                )}

                                {/* Registration Form */}
                                {!successMessage && (
                                    <form onSubmit={handleSubmit} noValidate>
                                        {/* Account Info Section */}
                                        <div className="form-section">
                                            <div className="section-header">
                                                <div className="section-icon">
                                                    <FontAwesomeIcon icon={faUser} />
                                                </div>
                                                <h3>Your Information</h3>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="emailInput" className="form-label">Email Address</label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="emailInput"
                                                    placeholder="you@example.com"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    disabled={formInputsDisabled}
                                                />
                                                <div className="form-text">Use the email your church has on file for you</div>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="nameInput" className="form-label">Full Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="nameInput"
                                                    placeholder="John Smith"
                                                    value={fullName}
                                                    onChange={(e) => setFullName(e.target.value)}
                                                    required
                                                    disabled={formInputsDisabled}
                                                />
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="passwordInput" className="form-label">Password</label>
                                                    <input
                                                        type="password"
                                                        className={`form-control ${password.length >= 6 ? 'is-valid' : ''}`}
                                                        id="passwordInput"
                                                        placeholder="Create a password"
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        required
                                                        disabled={formInputsDisabled}
                                                    />
                                                    <div className="form-text">At least 6 characters</div>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label htmlFor="confirmPasswordInput" className="form-label">Confirm Password</label>
                                                    <div className="password-input-wrapper">
                                                        <input
                                                            type="password"
                                                            className={`form-control ${confirmPassword ? (password === confirmPassword ? 'is-valid' : 'is-invalid') : ''}`}
                                                            id="confirmPasswordInput"
                                                            placeholder="Confirm password"
                                                            value={confirmPassword}
                                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                                            required
                                                            disabled={formInputsDisabled}
                                                        />
                                                        {confirmPassword && (
                                                            <span className={`password-match-icon ${password === confirmPassword ? 'match' : 'no-match'}`}>
                                                                <FontAwesomeIcon icon={password === confirmPassword ? faCheckCircle : faXmark} />
                                                            </span>
                                                        )}
                                                    </div>
                                                    {confirmPassword && password !== confirmPassword && (
                                                        <div className="password-mismatch-text">Passwords do not match</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="d-grid">
                                            <button type="submit" className="btn btn-create-account" disabled={formInputsDisabled}>
                                                {isLoading ? (
                                                    <>
                                                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                        Creating Account...
                                                    </>
                                                ) : (
                                                    "Create Account"
                                                )}
                                            </button>
                                        </div>

                                        {/* Free Note */}
                                        <p className="free-note">
                                            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                            Always free for church members
                                        </p>
                                    </form>
                                )}

                                {/* Footer Links */}
                                <div className="create-account-footer">
                                    <p>
                                        Already have an account? <Link to="/">Sign In</Link>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default CreateAccountPage;
